import { FFT_SIZE } from './spectrum';

/**
 * Web Audio graph around the playing `<audio>` element.
 *
 * ```
 * <audio> -> MediaElementSource -> AnalyserNode -> GainNode -> destination
 * ```
 *
 * The graph is an *optional enhancement*. If `AudioContext` is unavailable, or
 * `createMediaElementSource` throws because the media is not CORS-clean, playback must
 * carry on unaffected — the spectrum strip simply goes flat and volume falls back to
 * `HTMLMediaElement.volume`. Nothing about play, pause, seek or skip depends on it.
 *
 * There is deliberately no equaliser: the reference player has one, this does not.
 */

export type AudioEngine = {
  /** Linear gain, 0..1. Matches the desktop player, whose log taper was reverted. */
  setVolume(volume: number): void;
  /** Linear magnitudes for the current frame, or null when there is no analyser. */
  readSpectrum(): Float32Array | null;
  sampleRate(): number;
  /** Resume after an autoplay-policy suspension. Safe to call repeatedly. */
  resume(): Promise<void>;
  dispose(): void;
};

type GraphState = {
  context: AudioContext;
  analyser: AnalyserNode;
  gain: GainNode;
  // Explicitly backed by ArrayBuffer: getFloatFrequencyData rejects a SharedArrayBuffer
  // view, which is what the unparameterised Float32Array type would permit.
  decibels: Float32Array<ArrayBuffer>;
  magnitudes: Float32Array<ArrayBuffer>;
};

/**
 * A fallback that drives the element's own volume. Used when the graph cannot be
 * built, so callers never need to branch on whether audio processing is available.
 */
function elementOnlyEngine(element: HTMLAudioElement): AudioEngine {
  return {
    setVolume(volume) {
      element.volume = Math.min(Math.max(volume, 0), 1);
    },
    readSpectrum: () => null,
    sampleRate: () => 44_100,
    resume: async () => undefined,
    dispose: () => undefined,
  };
}

/**
 * A media element can only be attached to one source node for its whole lifetime, so
 * the graph is cached per element and reused across track changes.
 */
const graphs = new WeakMap<HTMLAudioElement, GraphState>();

export function createAudioEngine(element: HTMLAudioElement): AudioEngine {
  const AudioContextClass =
    typeof window === 'undefined'
      ? undefined
      : window.AudioContext ??
        (window as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
  if (!AudioContextClass) return elementOnlyEngine(element);

  let state = graphs.get(element);
  if (!state) {
    try {
      const context = new AudioContextClass();
      const source = context.createMediaElementSource(element);
      const analyser = context.createAnalyser();
      analyser.fftSize = FFT_SIZE;
      // Our own envelope lives in SpectrumAnalyzer; two smoothing passes would fight.
      analyser.smoothingTimeConstant = 0;
      const gain = context.createGain();
      source.connect(analyser);
      analyser.connect(gain);
      gain.connect(context.destination);
      state = {
        context,
        analyser,
        gain,
        decibels: new Float32Array(analyser.frequencyBinCount),
        magnitudes: new Float32Array(analyser.frequencyBinCount),
      };
      graphs.set(element, state);
    } catch {
      // CORS-tainted media, a blocked context, or an element already attached
      // elsewhere. None of these should stop the track from playing.
      return elementOnlyEngine(element);
    }
  }

  const graph = state;
  // The element's own volume stays wide open; the gain node is the single control.
  element.volume = 1;

  return {
    setVolume(volume) {
      const clamped = Math.min(Math.max(volume, 0), 1);
      graph.gain.gain.setTargetAtTime(clamped, graph.context.currentTime, 0.01);
    },
    readSpectrum() {
      graph.analyser.getFloatFrequencyData(graph.decibels);
      // `spectrum.rs` works in linear magnitude; the browser hands back dB.
      for (let index = 0; index < graph.decibels.length; index += 1) {
        graph.magnitudes[index] = Math.pow(10, graph.decibels[index]! / 20);
      }
      return graph.magnitudes;
    },
    sampleRate: () => graph.context.sampleRate,
    async resume() {
      if (graph.context.state === 'suspended') {
        await graph.context.resume().catch(() => undefined);
      }
    },
    dispose() {
      // The graph outlives any single track because the element does; tearing it down
      // here would make the next play() silent.
    },
  };
}
